import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formControlClass } from "../../../dashboardStyles";
import BrandingImageField from "../../BrandingImageField";
import InfoTip from "../components/InfoTip";
import SitePreview from "../../../../site-preview/SitePreview";
import { PlanLimits } from "../../../../../constants/subscription";
import { useObjectUrl } from "../../../../../hooks/useObjectUrl";
import { publicPagePath } from "../../../../../utils/publicPagePath";
import { initialsOf } from "../utils/initials";

const labelClass = "text-sm font-semibold text-ink-700";

/** A row that opens a panel under itself — address, appearance. */
const DisclosureRow = ({ id, icon, title, subtitle, open, onToggle, children }) => (
  <div className="rounded-lg border border-ink-200 bg-white">
    <button
      type="button"
      aria-expanded={open}
      aria-controls={id}
      onClick={onToggle}
      className="flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
    >
      <i className={`bi ${icon} text-xl text-ink-700`} aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-ink-900">{title}</span>
        {subtitle && (
          <span className="block truncate text-xs text-ink-500">{subtitle}</span>
        )}
      </span>
      <i
        className={`bi bi-chevron-right text-ink-400 transition-transform ${
          open ? "rotate-90" : ""
        }`}
        aria-hidden="true"
      />
    </button>
    {open && (
      <div id={id} className="border-t border-ink-200 p-3">
        {children}
      </div>
    )}
  </div>
);

DisclosureRow.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};

/** A paired colour swatch + hex field bound to the same form field. */
const ColorPicker = ({ id, label, value, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className="min-w-0">
      <label htmlFor={`${id}-text`} className="mb-1.5 block text-xs font-semibold text-ink-600">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="color"
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          aria-label={t(
            "Dashboard.Wizard.Steps.bookingPage.themeSection.colorPickerAriaLabel",
            { label },
          )}
          className="size-11 shrink-0 cursor-pointer rounded-lg border border-ink-200 bg-white p-1"
        />
        <input
          type="text"
          id={`${id}-text`}
          name={id}
          value={value}
          onChange={onChange}
          className={`${formControlClass} font-mono`}
        />
      </div>
    </div>
  );
};

ColorPicker.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

/**
 * Step 5: the booking page — a first pass, with the live page beside it. A
 * deliberately short subset of Site & widget: the title, a line of
 * introduction, logo and cover, the address customers see, and the two colours.
 *
 * The preview is the real public page (components/site-preview), not a sketch
 * of it: a business choosing its colours here sees them where its customers
 * will.
 */
const BookingPageStep = ({
  formData,
  onChange,
  onFileChange,
  logoFile,
  headerImageFile,
  disabled,
  onLogoBusyChange,
  onHeaderBusyChange,
  canPreview,
}) => {
  const { t } = useTranslation();
  const key = "Dashboard.Wizard.Steps.bookingPage";
  // Opened from the start when there is already an image to show.
  const [logoOpen, setLogoOpen] = useState(Boolean(formData.logoUrl));
  const [coverOpen, setCoverOpen] = useState(Boolean(formData.headerImageUrl));
  const [addressOpen, setAddressOpen] = useState(false);
  const [lookOpen, setLookOpen] = useState(false);

  const address = [formData.bookingCity, formData.streetAddress]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" · ");
  const pagePath = publicPagePath(formData.slug);

  // The real page, drawn from the step as it stands: images not uploaded yet
  // as blob: URLs, removed ones gone. The same template and the same mapping
  // the customers' page uses, so what is shown here is what they will see.
  const logoPreview = useObjectUrl(logoFile);
  const headerPreview = useObjectUrl(headerImageFile);
  const previewSettings = useMemo(
    () => ({
      ...formData,
      logoUrl: logoPreview || (formData.removeLogo ? "" : formData.logoUrl),
      headerImageUrl:
        headerPreview || (formData.removeHeaderImage ? "" : formData.headerImageUrl),
    }),
    [formData, logoPreview, headerPreview],
  );
  const resourceCount = formData.resources?.length ?? 0;
  const serviceCount = formData.services?.length ?? 0;

  const linkButtonClass =
    "inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg px-1 text-sm font-semibold text-brand-700 transition hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

  return (
    <div>
      <h2 className="m-0 text-2xl font-bold text-ink-900 sm:text-3xl">
        {t("Dashboard.Setup.page.title")}
      </h2>
      <p className="mt-1.5 mb-6 text-fluid-sm text-ink-500">
        {t("Dashboard.Setup.page.subtitle")}
      </p>

      {/* Two columns from `xl`: at 1024 the dashboard sidebar leaves the card
          about 600px, two 290px columns of form and preview. */}
      <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
        <section className="flex min-w-0 flex-col gap-5 rounded-card border border-ink-200 bg-white p-4 sm:p-5">
          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <label htmlFor="bookingTitle" className={labelClass}>
                {t("Dashboard.Setup.page.titleLabel")}
              </label>
              <InfoTip
                label={t("Dashboard.Setup.moreAbout", {
                  label: t("Dashboard.Setup.page.titleLabel"),
                })}
                text={t("Dashboard.Setup.page.titleTip")}
              />
            </div>
            <input
              type="text"
              id="bookingTitle"
              name="bookingTitle"
              value={formData.bookingTitle}
              onChange={onChange}
              maxLength={200}
              className={formControlClass}
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-1.5">
              <label htmlFor="heroDescription" className={labelClass}>
                {t("Dashboard.Setup.page.introLabel")}{" "}
                <span className="font-normal text-ink-400">
                  {t("Dashboard.Setup.optional")}
                </span>
              </label>
              <InfoTip
                label={t("Dashboard.Setup.moreAbout", {
                  label: t("Dashboard.Setup.page.introLabel"),
                })}
                text={t("Dashboard.Setup.page.introTip")}
              />
            </div>
            <textarea
              id="heroDescription"
              name="heroDescription"
              rows={3}
              value={formData.heroDescription}
              onChange={onChange}
              maxLength={500}
              className={`${formControlClass} resize-y`}
            />
            <p className="m-0 mt-1.5 text-xs text-ink-500">
              {t("Dashboard.Setup.page.introHint")}
            </p>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                aria-hidden="true"
                className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-base font-semibold text-brand-700"
              >
                {initialsOf(formData.businessName) || "—"}
              </span>
              <span className="text-sm font-semibold text-ink-700">
                {t("Dashboard.Setup.page.logo")}{" "}
                <span className="font-normal text-ink-400">
                  {t("Dashboard.Setup.optional")}
                </span>
              </span>
              <button
                type="button"
                aria-expanded={logoOpen}
                onClick={() => setLogoOpen((open) => !open)}
                className={linkButtonClass}
              >
                <i className="bi bi-upload" aria-hidden="true" />
                {t("Dashboard.Setup.page.uploadLogo")}
              </button>
              <button
                type="button"
                aria-expanded={coverOpen}
                onClick={() => setCoverOpen((open) => !open)}
                className={linkButtonClass}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                {t("Dashboard.Setup.page.addCover")}
              </button>
            </div>
            {logoOpen && (
              <div className="mt-2">
                <BrandingImageField
                  id="wizard-logoFile"
                  kind="logo"
                  label={t(`${key}.logoSection.title`)}
                  imageUrl={formData.logoUrl}
                  file={logoFile}
                  removed={formData.removeLogo}
                  disabled={disabled}
                  onBusyChange={onLogoBusyChange}
                  onChange={(file, removed) => onFileChange(file, removed, "logoUrl")}
                />
              </div>
            )}
            {coverOpen && (
              <div className="mt-2">
                <BrandingImageField
                  id="wizard-headerImageFile"
                  kind="header"
                  label={t(`${key}.headerImageSection.title`)}
                  imageUrl={formData.headerImageUrl}
                  file={headerImageFile}
                  removed={formData.removeHeaderImage}
                  disabled={disabled}
                  onBusyChange={onHeaderBusyChange}
                  onChange={(file, removed) =>
                    onFileChange(file, removed, "headerImageUrl")
                  }
                />
              </div>
            )}
          </div>

          <div>
            <DisclosureRow
              id="wizard-page-address"
              icon="bi-geo-alt"
              title={t("Dashboard.Setup.page.address")}
              subtitle={address || t("Dashboard.Setup.page.addressEmpty")}
              open={addressOpen}
              onToggle={() => setAddressOpen((open) => !open)}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="bookingCity" className="mb-1.5 block text-xs font-semibold text-ink-600">
                    {t(`${key}.addressSection.cityLabel`)}
                  </label>
                  <input
                    type="text"
                    id="bookingCity"
                    name="bookingCity"
                    value={formData.bookingCity}
                    onChange={onChange}
                    placeholder={t(`${key}.addressSection.cityPlaceholder`)}
                    className={formControlClass}
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="streetAddress" className="mb-1.5 block text-xs font-semibold text-ink-600">
                    {t(`${key}.addressSection.streetLabel`)}
                  </label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    value={formData.streetAddress}
                    onChange={onChange}
                    placeholder={t(`${key}.addressSection.streetPlaceholder`)}
                    className={formControlClass}
                  />
                </div>
              </div>
            </DisclosureRow>
            <p className="m-0 mt-1.5 text-xs text-ink-500">
              {t("Dashboard.Setup.page.addressHint")}
            </p>
          </div>

          <DisclosureRow
            id="wizard-page-look"
            icon="bi-palette"
            title={t("Dashboard.Setup.page.look")}
            subtitle={t("Dashboard.Setup.page.lookHint")}
            open={lookOpen}
            onToggle={() => setLookOpen((open) => !open)}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <ColorPicker
                id="primaryColor"
                label={t(`${key}.themeSection.primaryLabel`)}
                value={formData.primaryColor}
                onChange={onChange}
              />
              <ColorPicker
                id="secondaryColor"
                label={t(`${key}.themeSection.secondaryLabel`)}
                value={formData.secondaryColor}
                onChange={onChange}
              />
            </div>
            <p className="m-0 mt-3 text-xs text-ink-500">
              {t("Dashboard.Setup.page.fullEditorHint")}
            </p>
          </DisclosureRow>
        </section>

        <SitePreview
          settings={previewSettings}
          headingLevel={3}
          showsBadge={Boolean(PlanLimits[Number(formData.plan)]?.showsPlatformBadge)}
          actions={
            canPreview && pagePath ? (
              <a
                href={pagePath}
                target="_blank"
                rel="noopener noreferrer"
                title={t("Dashboard.Setup.page.previewLinkTitle")}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 text-xs font-semibold text-brand-700 no-underline hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1">
                  <i className="bi bi-eye" aria-hidden="true" />
                  {t("Dashboard.Setup.page.previewLink")}
                </span>
              </a>
            ) : null
          }
        />
      </div>

      <ul
        className="m-0 mt-6 flex list-none flex-wrap justify-center gap-x-8 gap-y-2 rounded-lg bg-brand-50 px-4 py-3 text-sm text-ink-800"
        aria-label={t("Dashboard.Setup.page.summaryLabel")}
      >
        {[
          t("Dashboard.Setup.page.summary.business"),
          t("Dashboard.Setup.page.summary.specialists", { count: resourceCount }),
          t("Dashboard.Setup.page.summary.services", { count: serviceCount }),
        ].map((line) => (
          <li key={line} className="flex items-center gap-2">
            <i className="bi bi-check-circle-fill text-brand-600" aria-hidden="true" />
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
};

BookingPageStep.propTypes = {
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  logoFile: PropTypes.object,
  headerImageFile: PropTypes.object,
  disabled: PropTypes.bool,
  onLogoBusyChange: PropTypes.func,
  onHeaderBusyChange: PropTypes.func,
  /** Whether the organization exists, so its public page has an address. */
  canPreview: PropTypes.bool,
};

export default BookingPageStep;
